import { RNG } from '../core/rng';
import { P } from '../world/player';
import { mechUpIds } from './mech-upgrades';
import { craftIds } from './loadout';
import { nearestCraftStepId, sampleDraft } from './draft';
import { metChest } from './metrics';
/* ============================================================
   寶箱＝三選一（高級的給五選三）
   ============================================================

   ## 這個檔換過兩次，兩次都是使用者拍板的

   1. 2026-08-04 早：固定三次三選一 → **VS 式拉霸**（開箱直接給 1／3／5 件）
   2. 2026-08-04 晚：拉霸 **整個拔掉** → 回到三選一，並且
      「甚至基於這去做變化 **五選三**（高級 boss）」

   拉霸的問題在它把寶箱從**決策**變成**結果**：走過去、開箱、看它給你什麼。
   而 `03-in-run-progression.md` 的第一句就是「三選一抽卡是這個類型的決策核心」。
   一場十幾個箱子全部交給運氣，玩家對 build 的掌控就整段消失了 ——
   而進化與融合**只從寶箱出**，所以消失的那一段正好是最重要的那一段。

   ## 現在的規格（2026-08-04 晚第二次調整，使用者拍板）

     銅箱（小怪 / 精英組員）  三選一        不出合成品
     銀箱（精英首領）         **四選二**    出合成品（2026-08-06 從五選三改）
     金箱（**Boss 擊破**）    **五選三**    出合成品 ＋ 保證一個機體武裝強化
                                            ＋ 史詩／傳說權重加倍

   五選三是「高級」的具體形狀：池子更大、而且**選三次**。它比「三選一 ×3」好，
   因為五張裡挑三張同時是**排除**（我不要剩下那兩張），
   而三選一連做三次只是重複三次同一個決策。

   ## Boss 獎勵從「直接彈出畫面」搬進金箱

   在這之前 Boss 死掉是 `P.bossUp++`，介面當場彈出機體武裝強化三選一。
   使用者要它變成金箱 —— 差別是**玩家要走過去撿**，而那正是這個專案
   一直在用的設計重點（「要不要為了那個箱子穿過敵群」）。
   Boss 死掉的當下場面最亂，把獎勵放在地上等於把決策延後到安全的那一刻。

   金箱的第一格保證是主／副／大招其中一個的強化（`sim/mech-upgrades.ts`），
   所以「打贏 Boss 一定拿得到機體強化」這件事沒有變 —— 變的只是交付方式。

   ## 抽選走 `sim/draft.ts` 的權重，不自己隨機

   寶箱自己均勻隨機抽的話，`data/rarity.ts` 的稀有度分層會被整個繞過去 ——
   傳說在寶箱裡的機率會變成「6 ÷ 池子大小」而不是表上的 1–10%。
   而且那個錯是靜默的：開出來的東西看起來完全正常，只有分佈是錯的。

   ## 幸運

   拉霸拔掉之後 `luck` 原本的出口（件數擲骰）跟著消失。它現在有兩個出口：
   `data/rarity.ts` 的史詩／傳說權重（既有），以及這裡的**池子加大** ——
   幸運高的人看到的選項比較多，而「選項多」正是 `05-meta-progression.md`
   說的那種「體感強但實際弱」的好屬性。 */

/** 小怪掉銅箱的**冷卻秒數**（`0` = 關掉，量測台用）。

    ---------- 2026-08-05：從 per-kill 機率換成冷卻 ----------

    舊寫法是每殺一隻 roll `0.0006`。問題是**它乘上的是擊殺率，而擊殺率
    一場之內從 1.5/秒 漲到 50/秒** —— 實測一場開箱 62 次、給了 48.5 次選擇
    （`pnpm metrics` 第 4b 區），每 20 秒就把畫面停下來一次。
    而且那 48.5 次選擇會把抽卡池吃乾，於是 15–20 分的升級全部被
    `ui/levelup.ts` 的空池分支丟掉（那一段的升級次數量到 0）。
    ——「丟掉」在 2026-08-05 改成發補給了，但**寶箱吃掉升級預算**這件事沒變：
    銀／金箱一箱吃 3 個升級步，這是池子見底的主因之一
    （供需推導見 `.claude/notes/enjoy-gap-2026-08-05.md`）。

    改成冷卻之後掉落節奏**與密度脫鉤**：不管場上 10 隻還是 300 隻，
    銅箱都是大約每 `trashCd` 秒一個。抖動 ±30% 讓它不要變成節拍器。

    ---------- 2026-08-06：改成**隨時間拉長**（60 → 150） ----------

    使用者實玩：「寶箱太太太多了」。一場的開箱中位數是 **57 次**
    （`pnpm metrics` 第 4b 區）＝ 每 21 秒就把畫面停下來一次。

    ⚠️ **第一版把它調成固定 110 秒，十顆種子全滅**（存活中位數
    15.5 → 4.8 分、通關 0/10、開箱只剩 6 次）。原因是「太多」是**後期**的
    問題，而固定值砍的是**整場** —— 前期玩家的成長幾乎全部來自這條線，
    砍一半等於讓他在武裝還沒成型的時候面對第 4 分鐘的密度。

    所以改成隨時間拉長：**前 8 分鐘維持 60 秒**（成長期不動），
    之後線性拉到 150 秒（後期玩家已經滿槽，箱子的邊際價值本來就低，
    而打斷的代價隨密度上升）。一場約 20 → 13 個。

    這是這個 codebase 的通則：**「太多／太少」幾乎都是分段問題，
    不是常數問題**（`data/pacing.ts` 的三張表是同一個道理）。

    同族的結構性坑：修復包（`sim/fx.ts` 的低血加成）也是 per-kill roll ——
    「每殺一隻 roll 一次」的東西在這個遊戲裡都會隨密度爆掉，
    **要嘛加冷卻、要嘛加自限條件**。 */
export var CHEST = { trashCd: 60, nextTrash: 0 };
/** 銅箱冷卻的後段拉長：`t0` 之後從 `trashCd` 線性拉到 `late` 秒。
    `trashCd = 0`（量測台關掉掉落）時整條一起關，見 `trashCdAt`。 */
export var CHEST_LATE = { t0: 8 * 60, t1: 16 * 60, late: 150 };

/** 這一刻的銅箱冷卻。 */
export function trashCdAt(t: number) {
  var base = CHEST.trashCd;
  if (base <= 0) return 0;                    // 量測台：關掉就是關掉
  if (t <= CHEST_LATE.t0) return base;
  var k = Math.min(1, (t - CHEST_LATE.t0) / (CHEST_LATE.t1 - CHEST_LATE.t0));
  return base + (CHEST_LATE.late - base) * k;
}

/** 每場開頭歸零。**模組層級的可變狀態一定要有重置入口** ——
    留著上一場的 `nextTrash`（可能是 1150 秒）會讓下一場前 19 分鐘一個銅箱都不掉，
    而且不會有任何錯誤。這個 codebase 記過一次「新欄位忘了在 startRun 歸零」。 */
export function resetChest() {
  /* **從一個完整冷卻之後才開始**，不是 0。設成 0 的話冷卻在開局就已經到期，
     於是「第一隻雜兵」必掉一個銅箱 —— 每一場都白送一個，而且
     `elites.test.ts` 的「普通敵人不掉寶箱」會紅（那條測試跑在 t≈0）。 */
  CHEST.nextTrash = CHEST.trashCd;
}

/** 箱種。`pool` 是給幾個選項、`picks` 是選幾次、`craft` 決定能不能出進化／融合。 */
export var TIERS: Record<string, {
  pool: number; picks: number; craft: boolean; n: string; col: number;
  /** 第一格保證塞一個機體武裝強化（主／副／大招）。只有金箱有。 */
  mech?: boolean;
  /** 史詩與傳說的權重倍率。金箱 ×2 —— Boss 的獎勵要看得出比較好。 */
  boost?: number;
}> = {
  bronze: { pool: 3, picks: 1, craft: false, n: '銅箱', col: 0xc98a52 },
  /* 銀箱＝精英首領。**四選二**（2026-08-06 使用者拍板）。
     三檔於是各有各的形狀：銅箱三選一、銀箱四選二、金箱五選三。

     **理由是體感不是配平**：五張裡挑三張的認知負擔太重，
     一場開五十幾個箱子的節奏下會變成選擇障礙。這一條數據推翻不了 ——
     量測量得到決策「次數」，量不到玩家在那五張前面停多久。

     ⚠️ 已知代價（十顆種子，`.claude/notes/craft-gate-2026-08-06.md`）：
     **質變 Lv1 只從寶箱出**，少給一張等於少一分供給。合成品本身仍然
     保證佔第一格（見下面的 `t.craft` 分支），被掐掉的是「湊齊配方的速度」
     —— 拿得少 → 練級慢 → `craftIds()` 空的時間變長。
     要補的話補在那一側（幫玩家湊齊），不要再回頭加箱子的張數。 */
  silver: { pool: 4, picks: 2, craft: true, n: '銀箱', col: 0xd8dde6 },
  /* 金箱＝**Boss 擊破**。五選三、保證一個機體武裝強化、史詩與傳說加倍。 */
  gold: { pool: 5, picks: 3, craft: true, n: '金箱', col: 0xffd24a, mech: true, boost: 2 }
};

/** 幸運最多把池子加大幾個。**要有上限** —— 選項無限多就不是取捨了，
    而且 03 明說 4 個以上會產生選擇疲勞並拉長暫停時間。 */
export var LUCK_POOL_MAX = 2;

/** 這一箱的池子要幾張。幸運每滿 1.0 加一張，上限 `LUCK_POOL_MAX`。 */
export function poolSize(tier: string) {
  var t = TIERS[tier] || TIERS.silver;
  var luck = (P.s && P.s.luck) ? P.s.luck : 1;
  var bonus = Math.floor(luck - 1);
  if (bonus < 0) bonus = 0;
  if (bonus > LUCK_POOL_MAX) bonus = LUCK_POOL_MAX;
  return t.pool + bonus;
}

/** 開箱：擲出這一箱的選項。**不套用** —— 套用在玩家選了之後（`ui/levelup.ts`）。

    這是與拉霸版本最大的結構差別。拉霸是「撿起來就套用完，介面只負責顯示」，
    理由是玩家在揭示途中死掉獎勵會消失；三選一沒有這個問題，
    因為介面會把 `STATE.reward` 設成 true 而主迴圈看到它就暫停模擬 ——
    選完之前玩家不會死。

    回傳 `{ ids, picks }`。`ids` 為空代表全滿級又滿槽，
    呼叫端要換成別的東西（**不要給玩家一個空箱子**）。 */
export function offerChest(tier: string, tMin: number) {
  var t = TIERS[tier] || TIERS.silver;
  var n = poolSize(tier);
  var out: string[] = [];
  /* 合成品（進化／融合）**佔第一格**，照 VS 官方 wiki 的 Evolution 頁。
     一箱最多一個 —— 五選三一次把兩個進化倒出來的話，
     「湊齊前置」那條線的張力會在一瞬間被清空。

     ⚠️ 與拉霸版不同的是它現在是**選項**不是贈品：玩家可以不選它。
     那是刻意的 —— 進化會取代來源武裝、融合會吃掉兩把，
     不是每一場、每個時間點都想要。 */
  /* **機體武裝強化佔第一格**（只有金箱）。放在合成品之前是因為
     「打贏 Boss 一定拿得到機體強化」是這個箱子存在的理由 ——
     池子見底時第一個被擠掉的不該是它。
     從三條線（主／副／大招）裡隨機挑一個：`mechUpIds()` 已經做過
     「每條線各出一張」的定向發牌，這裡取其中一張就好，
     不然五格會被三張機體強化吃掉一大半。 */
  if (t.mech) {
    var mu = mechUpIds();
    if (mu.length) out.push(mu[Math.floor(RNG.next() * mu.length) % mu.length]);
  }
  var hadCraft = false;
  if (t.craft) {
    var c = craftIds();
    if (c.length) {
      /* **同時湊齊好幾個時要隨機挑一個，不能永遠取第 0 個。**
         `craftIds()` 的順序就是 `EVOLUTIONS` 的陣列順序，所以寫死 `c[0]`
         等於「排在後面的那一條永遠不會出現在寶箱裡」。目前一把武裝只有
         一條進化路線，所以還看不出來；一對多之後這會讓第二條路線
         **完全拿不到**，而且沒有任何錯誤 —— 只是那個進化不存在。

         只有一個候選時不擲骰：這樣今天的行為（以及黃金雜湊）完全不變，
         RNG 的取用序列只在真的有選擇時才多消耗一個。 */
      out.push(c.length === 1 ? c[0] : c[Math.floor(RNG.next() * c.length) % c.length]);
      hadCraft = true;
    } else {
      /* **還沒有任何配方湊齊時，這一格放「朝合成品的一步」**（2026-08-06）。
         在這之前這一格就是普通抽卡，於是「保證第一格是合成品」這條規則
         在整場最需要導引的前半段等於不存在 —— 玩家看不出自己離質變多遠、
         也不知道該練哪一項。抽卡的 ×4 推力只在「差最後一級」才觸發。

         這一格不算 `hadCraft`：那個埋點問的是「第一格擺得出合成品嗎」，
         混進來的話報表上「擺得出合成品的箱數」會虛胖，而那個數字正是
         用來判斷質變供給夠不夠的。 */
      var step = nearestCraftStepId();
      if (step) out.push(step);
    }
  }
  var rest = sampleDraft(n - out.length, tMin, t.boost);
  for (var i = 0; i < rest.length; i++) {
    if (out.indexOf(rest[i]) < 0) out.push(rest[i]);
  }
  /* 選幾次不能多於選項數 —— 池子見底時（快滿級了）
     五選三會退化成「三選三」，那不是選擇是清單。 */
  var picks = Math.min(t.picks, out.length);
  /* `hadCraft` 記的是「第一格擺得出合成品」而不是「玩家拿了合成品」——
     後者由 `metPick` 記（寶箱與升級走同一個出口）。分不開的話，
     「進化出現了但玩家沒選」與「進化根本沒出現」會混成同一個數字。 */
  metChest(out.length, picks, hadCraft);
  return { ids: out, picks: picks };
}
